/**
 * `RelationshipsTab` — groups `RelationshipSummary[]` by edge type and
 * renders each edge as a three-lane row:
 *
 *   [source badge + name]  [← / edge-type / →]  [target badge + name]
 *
 * IN/OUT chips mark the pivot-relative direction when a pivot node is known.
 */

import { useMemo } from 'react'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import { cn } from '@/utils/cn'
import type { RelationshipSummary } from './types'

interface LegendLabel {
  label: string
  count: number
  color: string
  borderColor?: string
}

export interface RelationshipsTabProps {
  selectedNodeId: string | null
  relationships: RelationshipSummary[]
  legendLabels?: LegendLabel[]
  /** Relationships shown greyed: marked no longer valid or wrong (PRD-0075 R-C4). */
  fadedIds?: ReadonlySet<string>
}

function labelStyle(label: string, legend: LegendLabel[] | undefined): React.CSSProperties {
  const hit = legend?.find(l => l.label === label)
  if (hit) {
    return {
      backgroundColor: hit.color,
      color: 'var(--primary-foreground)',
      borderColor: hit.borderColor ?? hit.color,
    }
  }
  const colors = LABEL_COLORS[label] ?? LABEL_COLORS.Default
  if (!colors) {
    return { backgroundColor: 'var(--muted)', color: 'var(--foreground)' }
  }
  return { backgroundColor: colors.fill, color: '#ffffff', borderColor: colors.stroke }
}

export function RelationshipsTab({
  selectedNodeId,
  relationships,
  legendLabels,
  fadedIds,
}: RelationshipsTabProps) {
  const groups = useMemo(() => {
    const byType = new Map<string, RelationshipSummary[]>()
    for (const rel of relationships) {
      const bucket = byType.get(rel.type)
      if (bucket) bucket.push(rel)
      else byType.set(rel.type, [rel])
    }
    return Array.from(byType.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [relationships])

  if (relationships.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-muted-foreground">No relationships to show.</p>
    )
  }

  return (
    <div className="space-y-4">
      {groups.map(([type, rels]) => (
        <section key={type}>
          <div className="mb-2 flex items-center gap-2">
            <span className="inline-flex items-center rounded-md border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground">
              {type}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {rels.length} {rels.length === 1 ? 'edge' : 'edges'}
            </span>
          </div>
          <ul className="space-y-1.5">
            {rels.map(rel => (
              <RelationshipRow
                key={rel.id}
                rel={rel}
                pivotNodeId={selectedNodeId}
                legendLabels={legendLabels}
                faded={fadedIds?.has(rel.id) ?? false}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function RelationshipRow({
  rel,
  pivotNodeId,
  legendLabels,
  faded,
}: {
  rel: RelationshipSummary
  pivotNodeId: string | null
  legendLabels?: LegendLabel[]
  faded: boolean
}) {
  const sourceLabel = rel.sourceLabels[0] ?? 'Entity'
  const targetLabel = rel.targetLabels[0] ?? 'Entity'
  const isPivotSource = pivotNodeId != null && pivotNodeId === rel.sourceId
  const isPivotTarget = pivotNodeId != null && pivotNodeId === rel.targetId

  return (
    <li
      data-faded={faded || undefined}
      className={cn(
        'overflow-hidden rounded-md border border-border bg-muted/20',
        faded && 'opacity-50'
      )}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-stretch">
        <Endpoint
          heading={isPivotSource ? 'From (this)' : 'From'}
          name={rel.sourceName}
          label={sourceLabel}
          legendLabels={legendLabels}
          emphasise={isPivotSource}
          align="left"
        />
        <div className="flex flex-col items-center justify-center gap-0.5 border-x border-border/60 bg-background/40 px-3 py-2">
          {/* The group heading above shows the type in full, so the narrow phone lane may cut it. */}
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground max-md:max-w-24 max-md:truncate">
            {rel.type}
          </span>
          <Arrow />
          {pivotNodeId != null ? (
            <span className="text-[9px] font-semibold uppercase tracking-wider text-primary">
              {rel.direction === 'outgoing' ? 'OUT' : 'IN'}
            </span>
          ) : null}
        </div>
        <Endpoint
          heading={isPivotTarget ? 'To (this)' : 'To'}
          name={rel.targetName}
          label={targetLabel}
          legendLabels={legendLabels}
          emphasise={isPivotTarget}
          align="right"
        />
      </div>
    </li>
  )
}

function Endpoint({
  heading,
  name,
  label,
  legendLabels,
  emphasise,
  align,
}: {
  heading: string
  name: string
  label: string
  legendLabels?: LegendLabel[]
  emphasise: boolean
  align: 'left' | 'right'
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1 px-3 py-2',
        align === 'right' ? 'items-end text-right' : 'items-start text-left'
      )}
    >
      <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {heading}
      </span>
      <span
        className="inline-flex max-w-full items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
        style={labelStyle(label, legendLabels)}
        title={label}
      >
        <span className="truncate">{label}</span>
      </span>
      <span
        className={cn(
          'w-full truncate text-xs',
          emphasise ? 'font-semibold text-foreground' : 'text-foreground/90'
        )}
        title={name}
      >
        {name}
      </span>
    </div>
  )
}

function Arrow() {
  return (
    <svg
      aria-hidden="true"
      width="44"
      height="8"
      viewBox="0 0 44 8"
      fill="none"
      className="text-muted-foreground"
    >
      <title>arrow</title>
      <path
        d="M0 4 H36 M36 4 L32 1 M36 4 L32 7"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
